import React from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Trash2, Plus } from 'lucide-react'

interface PackageDimension {
  id: number
  name: string
  volume: number
}

interface EditablePackageDimensionsTableProps {
  dimensions: PackageDimension[]
  onChange: (dimensions: PackageDimension[]) => void
}

export default function EditablePackageDimensionsTable({
  dimensions,
  onChange,
}: EditablePackageDimensionsTableProps) {
  const handleAddDimension = () => {
    const newDimension: PackageDimension = {
      id: Date.now(), // Temporary ID for new dimensions
      name: '',
      volume: 0,
    }
    onChange([...dimensions, newDimension])
  }

  const handleUpdateDimension = (
    index: number,
    field: keyof PackageDimension,
    value: string | number
  ) => {
    const updatedDimensions = dimensions.map((dim, i) => {
      if (i === index) {
        return { ...dim, [field]: value }
      }
      return dim
    })
    onChange(updatedDimensions)
  }

  const handleRemoveDimension = (index: number) => {
    const updatedDimensions = dimensions.filter((_, i) => i !== index)
    onChange(updatedDimensions)
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium"></h4>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddDimension}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Package
        </Button>
      </div>

      {dimensions.length === 0 ? (
        <div className="text-muted-foreground text-xs pt-4 text-center py-4 border border-dashed rounded-md">
          No packages found. Click "Add Package" to create one.
        </div>
      ) : (
        <div className="border rounded-md">
          <div className="grid grid-cols-[1fr,120px,60px] gap-4 p-2 bg-muted/50 text-xs">
            <div>Name</div>
            <div>Volume (m³)</div>
            <div></div>
          </div>
          {dimensions.map((dimension, index) => (
            <div
              key={dimension.id}
              className="grid grid-cols-[1fr,120px,60px] gap-4 p-2 border-t"
            >
              <Input
                value={dimension.name}
                onChange={e =>
                  handleUpdateDimension(index, 'name', e.target.value)
                }
                placeholder="Package name"
                className="h-8"
              />
              <Input
                type="number"
                value={dimension.volume}
                onChange={e =>
                  handleUpdateDimension(
                    index,
                    'volume',
                    parseFloat(e.target.value) || 0
                  )
                }
                placeholder="0"
                min="0"
                step="any"
                className="h-8"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveDimension(index)}
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
